import { useEffect, useState } from 'react';
import { Typography, Card, Space, Spin, Alert, Button, Divider, Table, Tooltip } from 'antd';
import { ArrowLeftOutlined, CalendarOutlined, ShopOutlined, ShoppingCartOutlined, CreditCardOutlined, ExclamationCircleOutlined, FilePdfOutlined, EyeOutlined, LoadingOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../../services/api';
import { useMediaQuery } from "react-responsive";
import { useClient } from "../../context/ClientContext";
import { EstadoPedidoChip } from '../../shared/components/EstadoPedidoChip';
import { EstadoPagoChip } from '../../shared/components/EstadoPagoChip';

const { Title, Text } = Typography;

export const DetallePedido = () => {
  const [detalle, setDetalle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [blobUrls, setBlobUrls] = useState({});
  const navigate = useNavigate();
  const { id } = useParams();
  const { clientId , clientPath} = useClient();
  const isMobile = useMediaQuery({ query: '(max-width: 900px)' });

  useEffect(() => { fetchDetalle(); }, [id]); // eslint-disable-line

  useEffect(() => {
    const adjuntos = detalle?.pago?.adjuntos || [];
    adjuntos.forEach(async (adj) => {
      if (blobUrls[adj.id]) return;
      const url = await api.pagos.getAdjuntoBlob(adj.id);
      if (url) setBlobUrls(prev => ({ ...prev, [adj.id]: url }));
    });
  }, [detalle]); // eslint-disable-line

  const fetchDetalle = async () => {
    try {
      setLoading(true);
      const response = await api.pedidos.getDetalle(id);
      if (response.status === 'success') setDetalle(response.data.detalle);
      else setError(response.message || 'Error al cargar el detalle del pedido');
    } catch { setError('Error de conexión'); }
    finally { setLoading(false); }
  };

  const fmt = (fecha) => new Date(fecha).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  const fmtMoneda = (valor) => new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', minimumFractionDigits: 2 }).format(valor);
  const esPdf = (nombre) => nombre?.toLowerCase().endsWith('.pdf');

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}><Spin size="large" /></div>;

  if (error || !detalle) return (
    <div style={{ maxWidth: 1400, margin: '40px auto', padding: isMobile ? '0 16px' : '0 24px' }}>
      <Alert type="error" message={error || 'No se pudo cargar el detalle'} style={{ marginBottom: 16 }} />
      <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(clientPath('/mis-pedidos'))}>Volver a Mis Pedidos</Button>
    </div>
  );

  const pago = detalle.pago;

  const itemColumns = [
    {
      title: 'Producto', key: 'producto',
      render: (_, item) => (
        <div>
          <div style={{ fontWeight: 600, fontSize: 13 }}>{item.producto || 'Producto'}</div>
          {item.codigo && <Text type="secondary" style={{ fontSize: 12 }}>{item.codigo}</Text>}
        </div>
      ),
    },
    { title: 'Cant.', dataIndex: 'cantidad', key: 'cantidad', align: 'center', width: 70, render: (v) => <strong>{v}</strong> },
    { title: 'Precio unit.', dataIndex: 'precio_unit', key: 'precio_unit', align: 'right', width: 120, render: (v) => fmtMoneda(v) },
    { title: 'Total', dataIndex: 'total', key: 'total', align: 'right', width: 120, render: (v) => <strong>{fmtMoneda(v)}</strong> },
  ];

  return (
    <div style={{ maxWidth: 1400, margin: '40px auto', padding: isMobile ? '0 16px' : '0 24px', minHeight: '70vh' }}>
      <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(clientPath('/mis-pedidos'))} style={{ marginBottom: 24 }}>
        Volver a Mis Pedidos
      </Button>

      <div style={{ display: 'flex', gap: 24, flexDirection: isMobile ? 'column' : 'row' }}>

        {/* COL IZQ */}
        <div style={{ flex: 1 }}>
          <Card>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
              <Space>
                <div style={{ width: 48, height: 48, borderRadius: 8, background: '#fee', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <span style={{ fontSize: 24, color: '#ef1c25', fontWeight: 700 }}>#</span>
                </div>
                <Title level={4} style={{ margin: 0 }}>Pedido #{detalle.id_pedido}</Title>
              </Space>
              <EstadoPedidoChip nombre={detalle.estado_nombre} descripcion={detalle.estado_desc} color={detalle.estado_color} />
            </div>

            <Space size="large" wrap style={{ marginBottom: 24 }}>
              <Space><CalendarOutlined style={{ color: '#999' }} /><Text type="secondary">{fmt(detalle.fecha_pedido)}</Text></Space>
              {detalle.cliente && <Space><ShopOutlined style={{ color: '#999' }} /><Text type="secondary" strong>{detalle.cliente}</Text></Space>}
              <Space><ShoppingCartOutlined style={{ color: '#999' }} /><Text type="secondary">{detalle.total_items} {detalle.total_items === 1 ? 'producto' : 'productos'}</Text></Space>
            </Space>

            <Divider />

            <Text type="secondary" style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              PRODUCTOS ({detalle.total_items})
            </Text>

            <Table
              columns={itemColumns}
              dataSource={detalle.items || []}
              rowKey={(r, i) => i}
              size="small"
              pagination={false}
              style={{ marginTop: 12 }}
            />
          </Card>

          {/* Sección pago */}
          {pago && (
            <Card style={{ marginTop: 16 }}>
              <Space style={{ marginBottom: 16 }}>
                <CreditCardOutlined style={{ color: '#888' }} />
                <Text type="secondary" style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  INFORMACIÓN DE PAGO
                </Text>
              </Space>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {pago.metodo_nombre && <InfoRow label="Método" value={pago.metodo_nombre} />}
                <InfoRow label="Estado" value={<EstadoPagoChip nombre={pago.estado_nombre} descripcion={pago.estado_desc} color={pago.estado_color} />} />
                {pago.fecha_pago && <InfoRow label="Registrado" value={fmt(pago.fecha_pago)} />}
                {pago.fecha_actualizacion && <InfoRow label="Última actualización" value={fmt(pago.fecha_actualizacion)} />}
                {pago.descuento > 0 && <InfoRow label="Descuento por método" value={`${pago.descuento}%`} color="green" />}
                {pago.comision > 0 && <InfoRow label="Recargo por método" value={`${pago.comision}%`} color="#d97706" />}
                {pago.motivo_rechazo && (
                  <div style={{ marginTop: 8, padding: '10px 14px', background: '#fff3f3', borderRadius: 8, border: '1px solid #ffcdd2', display: 'flex', gap: 8 }}>
                    <ExclamationCircleOutlined style={{ color: '#e53935', flexShrink: 0, marginTop: 2 }} />
                    <div>
                      <Text style={{ fontWeight: 700, color: '#c62828', display: 'block', fontSize: 12 }}>Motivo de rechazo</Text>
                      <Text type="secondary">{pago.motivo_rechazo}</Text>
                    </div>
                  </div>
                )}
              </div>

              {pago.adjuntos?.length > 0 && (
                <>
                  <Divider style={{ margin: '16px 0' }} />
                  <Text type="secondary" style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    COMPROBANTE{pago.adjuntos.length > 1 ? 'S' : ''} ({pago.adjuntos.length})
                  </Text>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 12 }}>
                    {pago.adjuntos.map((adj) => {
                      const url = blobUrls[adj.id];
                      const pdf = esPdf(adj.adjunto);
                      return (
                        <div key={adj.id} style={{ border: '1px solid #e0e0e0', borderRadius: 8, overflow: 'hidden' }}>
                          {!pdf && url && (
                            <img
                              src={url}
                              alt={adj.adjunto}
                              style={{ width: '100%', maxHeight: 220, objectFit: 'cover', display: 'block', cursor: 'zoom-in' }}
                              onClick={() => window.open(url, '_blank')}
                            />
                          )}
                          <div style={{ padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 8, background: '#fafafa' }}>
                            {pdf && <FilePdfOutlined style={{ color: '#e53935' }} />}
                            <Text style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 12 }}>
                              {adj.adjunto}
                            </Text>
                            <Tooltip title="Abrir en nueva pestaña">
                              <Button
                                type="text" size="small"
                                icon={url ? <EyeOutlined /> : <LoadingOutlined />}
                                disabled={!url}
                                onClick={() => url && window.open(url, '_blank')}
                              />
                            </Tooltip>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </Card>
          )}
        </div>

        {/* COL DCHA - RESUMEN */}
        <div style={{ width: isMobile ? '100%' : 380 }}>
          <Card>
            <Space style={{ marginBottom: 24 }}>
              <div style={{ width: 6, height: 20, background: '#ef1c25', borderRadius: 3 }} />
              <Text strong style={{ textTransform: 'uppercase', fontSize: 13 }}>RESUMEN</Text>
            </Space>

            <div style={{ marginBottom: 24 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
                <Text type="secondary">Subtotal</Text>
                <Text strong>{fmtMoneda(detalle.subtotal)}</Text>
              </div>
              {/* Los importes salen de lo que se guardó al cobrar, no se recalculan:
                  si mañana cambia el descuento de la pasarela, el pedido tiene que
                  seguir diciendo lo que se cobró. */}
              {detalle.descuento_promos > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
                  <Text style={{ color: 'green' }}>Promociones</Text>
                  <Text strong style={{ color: 'green' }}>-{fmtMoneda(detalle.descuento_promos)}</Text>
                </div>
              )}
              {detalle.ajuste_pago !== 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
                  <Text style={{ color: detalle.ajuste_pago < 0 ? 'green' : '#d97706' }}>
                    {detalle.ajuste_pago < 0 ? 'Descuento' : 'Recargo'} por método de pago
                  </Text>
                  <Text strong style={{ color: detalle.ajuste_pago < 0 ? 'green' : '#d97706' }}>
                    {detalle.ajuste_pago < 0 ? '-' : '+'}{fmtMoneda(Math.abs(detalle.ajuste_pago))}
                  </Text>
                </div>
              )}
            </div>

            <Divider style={{ margin: '0 0 16px' }} />

            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Title level={5} style={{ margin: 0 }}>Total</Title>
              <Title level={4} style={{ margin: 0, color: '#ef1c25' }}>{fmtMoneda(detalle.total)}</Title>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

const InfoRow = ({ label, value, color }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
    <Typography.Text type="secondary">{label}</Typography.Text>
    {typeof value === 'string' ? (
      <Typography.Text style={{ fontWeight: 500, color: color || undefined }}>{value}</Typography.Text>
    ) : value}
  </div>
);
